
import { Link } from "react-router-dom";
import { Star, MapPin, Calendar } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { formatDistance } from "@/utils/distance";

interface MealCardProps {
  id: string;
  name: string;
  image: string;
  rating: number;
  reviews: number;
  distance: string | number;
  price: number;
  availabilitySlots?: any[];
  chefName: string;
  className?: string;
  preparationType?: 'scheduled' | 'on_demand';
  prepTimeHours?: number;
}

export const MealCard = ({
  id,
  name,
  image,
  rating,
  reviews,
  distance,
  price,
  availabilitySlots = [],
  chefName,
  className,
  preparationType = 'scheduled',
  prepTimeHours,
}: MealCardProps) => {
  console.log('MealCard props for:', name, {
    chefName, // Log chef name for debugging
    preparationType,
    prepTimeHours,
    availabilitySlots
  });

  const getShortDayName = (day: string) => {
    return day.slice(0, 3);
  };

  const formatConsecutiveDays = (days: string[]) => {
    if (!days || days.length <= 1) return days?.map(getShortDayName).join(', ') || '';
    
    const weekDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const sortedDays = days.sort((a, b) => weekDays.indexOf(a) - weekDays.indexOf(b));
    
    let result = [];
    let start = sortedDays[0];
    let prev = sortedDays[0];
    let consecutive = false;
    
    for (let i = 1; i <= sortedDays.length; i++) {
      const currentDay = sortedDays[i];
      const prevDayIndex = weekDays.indexOf(prev);
      const currentDayIndex = weekDays.indexOf(currentDay);
      
      if (i === sortedDays.length || currentDayIndex !== (prevDayIndex + 1) % 7) {
        if (consecutive) {
          result.push(`${getShortDayName(start)}-${getShortDayName(prev)}`);
        } else {
          result.push(getShortDayName(start));
        }
        if (i < sortedDays.length) {
          start = currentDay;
          consecutive = false;
        }
      } else {
        consecutive = true;
      }
      prev = currentDay;
    }
    
    return result.join(', ');
  };

  const formatAvailability = (slots: any[]) => {
    if (!slots || slots.length === 0) return null;

    const slot = slots[0];
    if (!slot || !slot.startTime) return null;

    try {
      if (slot.recurring && Array.isArray(slot.recurring.days)) {
        const formattedDays = formatConsecutiveDays(slot.recurring.days);
        return `Every ${formattedDays} @ ${slot.startTime}`;
      }

      if (slot.dateRange) {
        const startDate = parseISO(slot.dateRange.from);
        const endDate = parseISO(slot.dateRange.to);

        if (format(startDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')) {
          return `${format(startDate, 'MMM d')} @ ${slot.startTime}`;
        } else {
          return `${format(startDate, 'MMM d')}-${format(endDate, 'MMM d')} @ ${slot.startTime}`;
        }
      }

      return null;
    } catch (error) {
      console.error('Error formatting availability:', error);
      return null;
    }
  };

  const formatDistanceDisplay = (distance: string | number) => {
    if (typeof distance === 'string') {
      if (!distance || distance === "Calculating..." || distance.includes("undefined")) {
        return "Distance unavailable";
      }
      return distance;
    } else if (typeof distance === 'number') {
      return formatDistance(distance);
    }
    return "Distance unavailable";
  };

  // Use safe default for chef name
  const displayChefName = chefName && chefName !== ' ' ? chefName : 'Anonymous Chef';

  return (
    <Link to={`/meals/${id}`} className="block">
      <Card className={cn("group hover:shadow-lg transition-shadow h-full", className)}>
        <div className="aspect-[3/2] relative overflow-hidden rounded-lg">
          <img
            src={image}
            alt={name}
            className="absolute inset-0 w-full h-full object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
          />
          <Badge 
            variant="secondary" 
            className="absolute top-2 right-2 bg-orange-500 text-white"
          >
            ${price.toFixed(2)}
          </Badge>
        </div>
        <CardContent className="p-3">
          <div className="space-y-1">
            <div className="space-y-0.5">
              <h3 className="font-semibold text-sm text-primary line-clamp-2 min-h-[2.5rem]">{name}</h3>
              <p className="text-xs text-muted-foreground">By {displayChefName}</p>
            </div>

            <div className="flex items-center text-xs text-muted-foreground">
              <div className="flex items-center">
                <Star className="w-3 h-3 text-yellow-400 shrink-0" />
                <span className="ml-1">{rating.toFixed(1)}</span>
                <span className="ml-1">({reviews})</span>
              </div>
              <span className="mx-1.5">•</span>
              <div className="flex items-center">
                <MapPin className="w-3 h-3 text-primary shrink-0" />
                <span className="ml-1 truncate">
                  {formatDistanceDisplay(distance)}
                </span>
              </div>
            </div>

            {preparationType === 'on_demand' ? (
              <div className="flex items-center text-xs text-muted-foreground">
                <Calendar className="w-3 h-3 mr-1 text-primary shrink-0" />
                <span className="truncate">On demand</span>
                <span className="mx-1.5">•</span>
                <span className="truncate">{prepTimeHours}hr notice</span>
              </div>
            ) : (
              availabilitySlots && availabilitySlots.length > 0 && (
                <div className="flex items-center text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3 mr-1 text-primary shrink-0" />
                  <span className="truncate">{formatAvailability(availabilitySlots)}</span>
                </div>
              )
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
